import React, { useState } from 'react';
import { Outlet,  useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { setCurrentPage } from '../store/slices/uiSlice';
import { logout } from '../store/slices/authSlice';


import {
    HomeIcon,
    UsersIcon,
    ServerIcon,
    CogIcon,
    GlobeAltIcon,
    MoonIcon,
    SunIcon,
    ArrowRightOnRectangleIcon,
    ChartBarIcon,
    SignalIcon,
    ExclamationTriangleIcon,
    ComputerDesktopIcon,
    ArrowPathIcon,
    AdjustmentsHorizontalIcon,
    CloudArrowUpIcon,
    CircleStackIcon,
    RectangleGroupIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import AKSClusterSelector from '../components/monitoring/AKSClusterSelector';

const MainLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useSelector((state: RootState) => state.ui);
    const [expandedSections, setExpandedSections] = useState<string[]>(['monitoring']);

    const toggleSection = (section: string) => {
        if (expandedSections.includes(section)) {
            setExpandedSections(expandedSections.filter(s => s !== section));
        } else {
            setExpandedSections([...expandedSections, section]);
        }
    };
    // Check if a section is expanded
    const isSectionExpanded = (section: string) => {
        return expandedSections.includes(section);
    };

    // Check if a route is active or its parent is active
    const isRouteActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };
  // Update current page based on location
  React.useEffect(() => {
    const path = location.pathname.split('/')[1] || 'dashboard';
    dispatch(setCurrentPage(path));
  }, [location, dispatch]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/auth/login');
  };

  const handleToggleDarkMode = () => {
    dispatch({ type: 'ui/toggleDarkMode' });
  };

    // Navigation sections.
    // status: 'verified' = tested and working | 'partial' = work in progress | 'unverified' = not yet tested
    const navSections = [
        {
            label: 'Tenant Management',
            status: 'verified' as const,
            items: [
                { name: 'Dashboard',       path: '/dashboard',       icon: <HomeIcon className="w-5 h-5" /> },
                { name: 'Tenants',         path: '/tenants',         icon: <UsersIcon className="w-5 h-5" /> },
                { name: 'Service Configs', path: '/service-configs', icon: <AdjustmentsHorizontalIcon className="w-5 h-5" /> },
                { name: 'Object Types',    path: '/object-types',    icon: <RectangleGroupIcon className="w-5 h-5" /> },
                { name: 'Db Clusters',     path: '/clusters',        icon: <CircleStackIcon className="w-5 h-5" /> },
            ],
        },
        {
            label: 'Infrastructure',
            status: 'partial' as const,
            items: [
                { name: 'Image Deploy',       path: '/deployment',          icon: <CloudArrowUpIcon className="w-5 h-5" /> },
                { name: 'Environments',       path: '/environments',        icon: <RectangleGroupIcon className="w-5 h-5" /> },
            ],
        },
        {
            label: 'Operations',
            status: 'unverified' as const,
            items: [
                {
                    name: 'Monitoring',
                    path: '/monitoring',
                    icon: <ChartBarIcon className="w-5 h-5" />,
                    children: [
                        { name: 'Container Apps',   path: '/monitoring',        icon: <ComputerDesktopIcon className="w-4 h-4" /> },
                        { name: 'Active Alerts',    path: '/monitoring/alerts', icon: <ExclamationTriangleIcon className="w-4 h-4" /> },
                        { name: 'Status Dashboard', path: '/monitoring/status', icon: <SignalIcon className="w-4 h-4" /> },
                    ],
                },
                { name: 'Kubernetes',      path: '/kubernetes/explorer', icon: <SignalIcon className="w-5 h-5" /> },
                { name: 'Cache',           path: '/cache-management',    icon: <ArrowPathIcon className="w-5 h-5" /> },
                { name: 'Domains',         path: '/domains',             icon: <GlobeAltIcon className="w-5 h-5" /> },
            ],
        },
        {
            label: 'Settings',
            status: 'unverified' as const,
            items: [
                { name: 'Configurations',       path: '/configurations',        icon: <CogIcon className="w-5 h-5" /> },
                { name: 'Settings',             path: '/settings',              icon: <CogIcon className="w-5 h-5" /> },
                { name: 'Provisioning Defaults', path: '/provisioning-defaults', icon: <WrenchScrewdriverIcon className="w-5 h-5" /> },
            ],
        },
    ];

    const sectionDot: Record<string, string> = {
        verified:   'bg-green-500',
        partial:    'bg-amber-400',
        unverified: 'bg-gray-300 dark:bg-gray-600',
    };
    const sectionLabel: Record<string, string> = {
        verified:   'text-green-700 dark:text-green-400',
        partial:    'text-amber-600 dark:text-amber-400',
        unverified: 'text-gray-400 dark:text-gray-500',
    };

    return (
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
            {/* Sidebar */}
            <div className="hidden md:flex md:flex-shrink-0">
                <div className="flex flex-col w-64">
                    <div className="flex flex-col flex-grow pt-5 overflow-y-auto bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                        <div className="flex items-center flex-shrink-0 px-4">
                            <img className="h-8 w-auto" src="/logo.svg" alt="Logo" />
                            <span className="ml-2 text-xl font-semibold text-gray-800 dark:text-white">Management Portal</span>
                        </div>
                        {/* AKS Cluster Selector */}
                        <div className="mt-4 border-b border-gray-200 dark:border-gray-700 pb-4">
                            <AKSClusterSelector />
                        </div>
                        <div className="mt-6 flex-grow flex flex-col">
                            <nav className="flex-1 px-2 pb-4 space-y-4">
                                {navSections.map((section) => (
                                    <div key={section.label}>
                                        {/* Section header */}
                                        <div className="flex items-center gap-1.5 px-2 mb-1">
                                            <span className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${sectionDot[section.status]}`} />
                                            <span className={`text-xs font-semibold uppercase tracking-wider ${sectionLabel[section.status]}`}>
                                                {section.label}
                                            </span>
                                        </div>

                                        <div className="space-y-0.5">
                                            {section.items.map((item: any) => (
                                                <div key={item.name}>
                                                    {item.children ? (
                                                        <>
                                                            <button
                                                                onClick={() => toggleSection(item.name.toLowerCase())}
                                                                className={`${isRouteActive(item.path)
                                                                    ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                                                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                                                                } group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md w-full`}
                                                            >
                                                                <div className="flex items-center">
                                                                    {item.icon}
                                                                    <span className="ml-3">{item.name}</span>
                                                                </div>
                                                                <svg className={`${isSectionExpanded(item.name.toLowerCase()) ? 'rotate-90' : ''} w-4 h-4 transition-transform`}
                                                                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </button>
                                                            {isSectionExpanded(item.name.toLowerCase()) && (
                                                                <div className="ml-4 mt-0.5 space-y-0.5">
                                                                    {item.children.map((child: any) => (
                                                                        <NavLink key={child.name} to={child.path}
                                                                            className={({ isActive }) =>
                                                                                `${isActive ? 'bg-primary-50 text-primary-900 dark:bg-primary-900 dark:text-primary-100' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`
                                                                            }>
                                                                            {child.icon}
                                                                            <span className="ml-3">{child.name}</span>
                                                                        </NavLink>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : item.badge === 'soon' ? (
                                                        // Coming-soon item — not yet navigable
                                                        <div className="flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-400 dark:text-gray-600 cursor-default select-none">
                                                            {item.icon}
                                                            <span className="ml-3 flex-1">{item.name}</span>
                                                            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500">soon</span>
                                                        </div>
                                                    ) : (
                                                        <NavLink to={item.path}
                                                            className={({ isActive }) =>
                                                                `${isActive ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`
                                                            }>
                                                            {item.icon}
                                                            <span className="ml-3">{item.name}</span>
                                                        </NavLink>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </nav>
                        </div>
                        <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
                            <button
                                onClick={handleToggleDarkMode}
                                className="flex-shrink-0 group block text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                {darkMode ? (
                                    <SunIcon className="h-6 w-6" />
                                ) : (
                                    <MoonIcon className="h-6 w-6" />
                                )}
                            </button>
                            <button
                                onClick={handleLogout}
                                className="ml-auto flex-shrink-0 group block text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                <ArrowRightOnRectangleIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Mobile top header/nav */}
                <div className="md:hidden flex items-center justify-between p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <img className="h-8 w-auto" src="/logo.svg" alt="Logo" />
                        <span className="ml-2 text-xl font-semibold text-gray-800 dark:text-white">Management Portal</span>
                    </div>
                    {/* Mobile menu button here */}
                </div>

                {/* Main content */}
                <main className="flex-1 relative overflow-y-auto focus:outline-none">
                    <div className="py-6">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};


export default MainLayout;